import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import { createPortal } from 'react-dom';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faLaptop,
    faTv,
    faMobileAlt,
    faLaptopCode,
    faFootballBall,
    faMicrochip,
    faDesktop,
    faGamepad,
    faCamera,
    faCompactDisc,
    faHouse,
    faToolbox,
    faMountain,
    faPrint,
    faBottleDroplet,
    faGem,
    faFireBurner,
    faSeedling,
    faPercent,
    faTags,
    faBolt,
    faArrowRight,
    faVrCardboard,
} from '@fortawesome/free-solid-svg-icons';

import { faApple } from '@fortawesome/free-brands-svg-icons';

import useGetMenuCategories from '~/hooks/useGetMenuCategories';
import useGetProducts from '~/hooks/useGetProducts';
import { DEFAULT_QUERY_GET_PRODUCTS } from '~/services/queries/productStrapiQueries';
import { ProductGroupWithCarousel } from '~/components/partials/product/ProductGroupWithCarousel';

const MAX_SUB_ITEMS = 5;
const DROPDOWN_GAP = 4;
const DROPDOWN_SIDE_MARGIN = 15;
const DROPDOWN_MAX_WIDTH = 1620;

const SALE_CATEGORY_SLUG = 'special-offer-sale';
const SALE_PAGE_HREF = '/sale';

const SALE_CATEGORY = {
    id: 'sale-static',
    documentId: 'sale-static',
    title: '% Sale',
    slug: SALE_CATEGORY_SLUG,
    icon: null,
    children: [],
    productBrands: [],
    topProduct: null,
};

const SALE_USPS = [
    { icon: faTags, text: 'Bis zu -50% auf ausgewählte Artikel' },
    { icon: faBolt, text: 'Täglich neue Angebote' },
    { icon: faFireBurner, text: 'Nur solange der Vorrat reicht' },
];

const OCCASION_CHIPS = {
    elektronik: [
        {
            key: 'gaming',
            label: 'Gaming',
            filters: { category: { slug: { $eq: 'elektronik-gaming' } } },
        },
        {
            key: 'smart-home',
            label: 'Smart Home',
            filters: { category: { slug: { $eq: 'elektronik-smart-home' } } },
        },
        {
            key: 'apple',
            label: 'Apple',
            filters: { category: { slug: { $eq: 'elektronik-apple' } } },
        },
        {
            key: 'tv-audio',
            label: 'TV & Audio',
            filters: { category: { slug: { $eq: 'elektronik-tv-audio' } } },
        },
    ],
    haushalt: [
        {
            key: 'staubsauger',
            label: 'Staubsauger',
            filters: {
                category: {
                    slug: { $eq: 'haushalt-haushaltskleingeraete-staubsauger' },
                },
            },
        },
        {
            key: 'kaffeemaschinen',
            label: 'Kaffeemaschinen',
            filters: {
                category: {
                    slug: { $eq: 'haushalt-kuechengeraete-kaffeemaschinen' },
                },
            },
        },
        {
            key: 'wellness',
            label: 'Wellness',
            filters: { category: { slug: { $eq: 'haushalt-wellness' } } },
        },
        {
            key: 'kochgeschirr',
            label: 'Kochgeschirr',
            filters: { category: { slug: { $eq: 'haushalt-kochgeschirr' } } },
        },
    ],
    garten: [
        {
            key: 'rasenpflege',
            label: 'Rasenpflege',
            filters: { category: { slug: { $eq: 'garten-rasenpflege' } } },
        },
        {
            key: 'bewaesserung',
            label: 'Bewässerung',
            filters: { category: { slug: { $eq: 'garten-bewaesserung' } } },
        },
        {
            key: 'pflanzenpflege',
            label: 'Pflanzenpflege',
            filters: { category: { slug: { $eq: 'garten-pflanzenpflege' } } },
        },
        {
            key: 'gartenmoebel',
            label: 'Gartenmöbel',
            filters: { category: { slug: { $eq: 'garten-gartenmoebel' } } },
        },
    ],
    grill: [
        {
            key: 'gasgrills',
            label: 'Gasgrills',
            filters: {
                category: { slug: { $eq: 'grill-grillart-gasgrills' } },
            },
        },
        {
            key: 'grillzubehoer',
            label: 'Zubehör',
            filters: { category: { slug: { $eq: 'grill-grillzubehoer' } } },
        },
        {
            key: 'grill-marken',
            label: 'Marken',
            filters: { category: { slug: { $eq: 'grill-grill-marken' } } },
        },
        {
            key: 'grillgewuerze',
            label: 'Gewürze & Saucen',
            filters: {
                category: {
                    slug: { $eq: 'grill-grillgewuerze---saucen' },
                },
            },
        },
    ],
    outdoor: [
        {
            key: 'zelte',
            label: 'Zelte',
            filters: { category: { slug: { $eq: 'outdoor-zelte' } } },
        },
        {
            key: 'schlafsaecke',
            label: 'Schlafsäcke',
            filters: { category: { slug: { $eq: 'outdoor-schlafsaecke' } } },
        },
        {
            key: 'rucksaecke',
            label: 'Rucksäcke',
            filters: {
                category: { slug: { $eq: 'outdoor-outdoor-rucksaecke' } },
            },
        },
        {
            key: 'campingkueche',
            label: 'Campingküche',
            filters: { category: { slug: { $eq: 'outdoor-campingkueche' } } },
        },
    ],
    werkzeug: [
        {
            key: 'elektrowerkzeuge',
            label: 'Elektrowerkzeuge',
            filters: {
                category: { slug: { $eq: 'werkzeug-elektrowerkzeuge' } },
            },
        },
        {
            key: 'handwerkzeug',
            label: 'Handwerkzeug',
            filters: { category: { slug: { $eq: 'werkzeug-handwerkzeug' } } },
        },
        {
            key: 'arbeitsschutz',
            label: 'Arbeitskleidung',
            filters: {
                category: {
                    slug: { $eq: 'werkzeug-arbeitskleidung--arbeitsschutz' },
                },
            },
        },
        {
            key: 'kfz-werkzeug',
            label: 'KFZ-Werkzeug',
            filters: { category: { slug: { $eq: 'werkzeug-kfz-werkzeug' } } },
        },
    ],
    spielzeug: [
        {
            key: 'gesellschaftsspiele',
            label: 'Gesellschaftsspiele',
            filters: {
                category: { slug: { $eq: 'spielzeug-gesellschaftsspiele' } },
            },
        },
        {
            key: 'puppen',
            label: 'Puppen & Figuren',
            filters: {
                category: {
                    slug: { $eq: 'spielzeug-puppen--spielfiguren' },
                },
            },
        },
        {
            key: 'outdoor-spielzeug',
            label: 'Draußen spielen',
            filters: {
                category: { slug: { $eq: 'spielzeug-outdoor-spielzeug' } },
            },
        },
        {
            key: 'lernen',
            label: 'Lernen & Experimentieren',
            filters: {
                category: {
                    slug: { $eq: 'spielzeug-lernen--experimentieren' },
                },
            },
        },
    ],
    schmuck: [
        {
            key: 'damenringe',
            label: 'Damenringe',
            filters: {
                category: { slug: { $eq: 'schmuck-ringe-damenringe' } },
            },
        },
        {
            key: 'herrenring',
            label: 'Herrenring',
            filters: {
                category: { slug: { $eq: 'schmuck-ringe-herrenring' } },
            },
        },
        {
            key: 'damenuhren',
            label: 'Damenuhren',
            filters: {
                category: { slug: { $eq: 'schmuck-uhren-damenuhren' } },
            },
        },
        {
            key: 'herrenuhren',
            label: 'Herrenuhren',
            filters: {
                category: { slug: { $eq: 'schmuck-uhren-herrenuhren' } },
            },
        },
    ],
    software: [
        {
            key: 'office',
            label: 'Microsoft Office',
            filters: {
                category: { slug: { $eq: 'software-microsoft-office' } },
            },
        },
        {
            key: 'betriebssysteme',
            label: 'Betriebssysteme',
            filters: {
                category: { slug: { $eq: 'software-betriebssysteme' } },
            },
        },
        {
            key: 'server-cal',
            label: 'Server & CAL',
            filters: {
                category: { slug: { $eq: 'software-server--cal' } },
            },
        },
    ],
};

const OCCASION_PREVIEW_LIMIT = 3;

function OccasionExplorer({
    categorySlug,
    getStrapiProducts,
}) {
    const chips = OCCASION_CHIPS[categorySlug] ?? [];

    const [activeChipKey, setActiveChipKey] = useState(null);
    const [previewProducts, setPreviewProducts] = useState(null);
    const [isLoadingChip, setIsLoadingChip] = useState(false);

    const chipCache = useRef({});
    const activeChipRef = useRef(null);

    useEffect(() => {
        setActiveChipKey(null);
        setPreviewProducts(null);
        setIsLoadingChip(false);
        activeChipRef.current = null;
        chipCache.current = {};
    }, [categorySlug]);

    if (chips.length === 0) {
        return null;
    }

    const handleChipClick = async (chip) => {
        if (activeChipKey === chip.key) {
            setActiveChipKey(null);
            setPreviewProducts(null);
            activeChipRef.current = null;
            return;
        }

        setActiveChipKey(chip.key);
        activeChipRef.current = chip.key;

        if (chipCache.current[chip.key]) {
            setPreviewProducts(chipCache.current[chip.key]);
            return;
        }

        setIsLoadingChip(true);
        setPreviewProducts(null);

        try {
            const result = await getStrapiProducts({
                ...DEFAULT_QUERY_GET_PRODUCTS,
                pagination: {
                    ...(DEFAULT_QUERY_GET_PRODUCTS.pagination ?? {}),
                    limit: OCCASION_PREVIEW_LIMIT,
                },
                filters: chip.filters,
            });

            if (activeChipRef.current !== chip.key) {
                return;
            }

            const items = Array.isArray(result)
                ? result
                : result?.data ?? [];

            chipCache.current[chip.key] = items;
            setPreviewProducts(items);
        } catch (err) {
            if (activeChipRef.current === chip.key) {
                setPreviewProducts([]);
            }
        } finally {
            if (activeChipRef.current === chip.key) {
                setIsLoadingChip(false);
            }
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <div
                style={{
                    fontWeight: 'bold',
                    marginBottom: 10,
                    fontSize: 13,
                }}
            >
                Wonach suchst du?
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                }}
            >
                {chips.map((chip) => {
                    const isActive = activeChipKey === chip.key;

                    return (
                        <button
                            key={chip.key}
                            type="button"
                            onClick={() => handleChipClick(chip)}
                            style={{
                                padding: '6px 14px',
                                borderRadius: 20,
                                border: isActive
                                    ? '1.5px solid #1976d2'
                                    : '1px solid #ddd',
                                background: isActive
                                    ? '#e3f2fd'
                                    : '#fff',
                                color: isActive
                                    ? '#1976d2'
                                    : '#333',
                                fontSize: 12.5,
                                fontWeight: isActive ? 600 : 500,
                                cursor: 'pointer',
                                transition:
                                    'background 0.15s, border-color 0.15s',
                            }}
                        >
                            {chip.label}
                        </button>
                    );
                })}
            </div>

            {activeChipKey && (
                <div
                    style={{
                        marginTop: 14,
                        minHeight: 72,
                    }}
                >
                    {isLoadingChip && (
                        <div
                            style={{
                                fontSize: 12.5,
                                color: '#888',
                            }}
                        >
                            Passende Produkte werden geladen …
                        </div>
                    )}

                    {!isLoadingChip &&
                        previewProducts &&
                        previewProducts.length === 0 && (
                            <div
                                style={{
                                    fontSize: 12.5,
                                    color: '#888',
                                }}
                            >
                                Aktuell keine passenden Produkte
                                gefunden.
                            </div>
                        )}

                    {!isLoadingChip &&
                        previewProducts &&
                        previewProducts.length > 0 && (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: 10,
                                }}
                            >
                                {previewProducts
                                    .slice(0, OCCASION_PREVIEW_LIMIT)
                                    .map((product, index) => {
                                        const attrs =
                                            product?.attributes ??
                                            product ??
                                            {};

                                        const key =
                                            product?.id ??
                                            product?.documentId ??
                                            index;

                                        const href = attrs.slug
                                            ? `/product/${attrs.slug}`
                                            : '#';

                                        return (
                                            <Link
                                                key={key}
                                                href={href}
                                                style={{
                                                    flex: '1 1 0',
                                                    minWidth: 0,
                                                    textDecoration:
                                                        'none',
                                                    color: 'inherit',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        aspectRatio:
                                                            '1 / 1',
                                                        borderRadius: 6,
                                                        border:
                                                            '1px solid #eee',
                                                        background:
                                                            '#fafbfc',
                                                        overflow:
                                                            'hidden',
                                                        display: 'flex',
                                                        alignItems:
                                                            'center',
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                >
                                                    {attrs.imageUrl ? (
                                                        <img
                                                            src={
                                                                attrs.imageUrl
                                                            }
                                                            alt={
                                                                attrs.title ??
                                                                ''
                                                            }
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit:
                                                                    'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <span
                                                            style={{
                                                                fontSize: 10,
                                                                color:
                                                                    '#999',
                                                            }}
                                                        >
                                                            Kein Bild
                                                        </span>
                                                    )}
                                                </div>

                                                <div
                                                    style={{
                                                        marginTop: 4,
                                                        fontSize: 11.5,
                                                        lineHeight: 1.3,
                                                        overflow: 'hidden',
                                                        display:
                                                            '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient:
                                                            'vertical',
                                                    }}
                                                >
                                                    {attrs.title}
                                                </div>

                                                {attrs.price && (
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        {attrs.price} €
                                                    </div>
                                                )}
                                            </Link>
                                        );
                                    })}
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------

const ICON_MAP = {
    'fa-solid fa-vr-cardboard': faVrCardboard,
    'fas fa-vr-cardboard': faVrCardboard,

    'fa-solid fa-microchip': faMicrochip,
    'fas fa-microchip': faMicrochip,

    'fa-solid fa-desktop': faDesktop,
    'fas fa-desktop': faDesktop,

    'fa-solid fa-laptop': faLaptop,
    'fas fa-laptop': faLaptop,

    'fa-solid fa-gamepad': faGamepad,
    'fa-regular fa-gamepad': faGamepad,
    'fas fa-gamepad': faGamepad,

    'fa-solid fa-tv': faTv,
    'fas fa-tv': faTv,

    'fa-solid fa-mobile-alt': faMobileAlt,
    'fas fa-mobile-alt': faMobileAlt,

    'fa-brands fa-apple': faApple,
    'fab fa-apple': faApple,

    'fa-solid fa-camera': faCamera,
    'fa-regular fa-camera': faCamera,
    'fas fa-camera': faCamera,

    'fa-solid fa-compact-disc': faCompactDisc,
    'fas fa-compact-disc': faCompactDisc,

    'fa-solid fa-laptop-code': faLaptopCode,
    'fas fa-laptop-code': faLaptopCode,

    'fa-solid fa-football-ball': faFootballBall,
    'fas fa-football-ball': faFootballBall,

    'fa-solid fa-house': faHouse,
    'fas fa-house': faHouse,

    'fa-solid fa-toolbox': faToolbox,
    'fas fa-toolbox': faToolbox,

    'fa-solid fa-mountain': faMountain,
    'fas fa-mountain': faMountain,

    'fa-solid fa-print': faPrint,
    'fas fa-print': faPrint,

    'fa-solid fa-bottle-droplet': faBottleDroplet,
    'fas fa-bottle-droplet': faBottleDroplet,

    'fa-solid fa-gem': faGem,
    'fa-regular fa-gem': faGem,
    'fas fa-gem': faGem,

    'fa-solid fa-fire-burner': faFireBurner,
    'fas fa-fire-burner': faFireBurner,

    'fa-solid fa-seedling': faSeedling,
    'fas fa-seedling': faSeedling,
};

function getIconComponent(iconName) {
    return ICON_MAP[iconName] ?? null;
}

function getProductAttributes(product) {
    return product?.attributes ?? product ?? {};
}

function getCategoryUrl(category) {
    if (category?.slug === SALE_CATEGORY_SLUG) {
        return SALE_PAGE_HREF;
    }

    return category?.slug
        ? `/category/${category.slug}`
        : '#';
}

function getBrandUrl(brand) {
    return brand?.slug
        ? `/brand/${brand.slug}`
        : '#';
}

function getEntityKey(entity, fallback) {
    return (
        entity?.id ??
        entity?.documentId ??
        entity?.slug ??
        fallback
    );
}

export default function MenuCategoriesDropdown() {
    const {
        categories = [],
        loading,
        error,
    } = useGetMenuCategories();

    const categoriesWithSale = [
        ...categories,
        SALE_CATEGORY,
    ];

    const {
        products,
        getStrapiProducts,
    } = useGetProducts();

    const [mounted, setMounted] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const [expandedSections, setExpandedSections] = useState({});
    const [topProductData, setTopProductData] = useState(null);
    const [dropdownStyle, setDropdownStyle] = useState(null);

    const menuRef = useRef(null);
    const toggleRef = useRef(null);
    const dropdownRef = useRef(null);

    const closeTimeout = useRef(null);
    const productCache = useRef({});
    const currentSkuRef = useRef(null);

    const isActiveCategorySale =
        activeCategory?.slug === SALE_CATEGORY_SLUG;

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        return () => {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
        };
    }, []);

    useEffect(() => {
        if (
            isOpen &&
            !activeCategory &&
            categoriesWithSale.length > 0
        ) {
            setActiveCategory(categoriesWithSale[0]);
        }
    }, [
        isOpen,
        activeCategory,
        categoriesWithSale,
    ]);

    const updateDropdownPosition = useCallback(() => {
        if (!toggleRef.current) {
            return;
        }

        const headerEl = toggleRef.current.closest('header');

        const referenceRect = (
            headerEl ?? toggleRef.current
        ).getBoundingClientRect();

        const viewportWidth =
            document.documentElement.clientWidth;

        const availableWidth =
            viewportWidth -
            DROPDOWN_SIDE_MARGIN * 2;

        const dropdownWidth = Math.min(
            DROPDOWN_MAX_WIDTH,
            availableWidth
        );

        setDropdownStyle({
            position: 'fixed',
            top: referenceRect.bottom + DROPDOWN_GAP,
            left: '50%',
            width: `${dropdownWidth}px`,
            maxWidth: `calc(100vw - ${
                DROPDOWN_SIDE_MARGIN * 2
            }px)`,
            transform: 'translateX(-50%)',
        });
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        updateDropdownPosition();

        const handleViewportChange = () => {
            updateDropdownPosition();
        };

        window.addEventListener(
            'resize',
            handleViewportChange
        );

        window.addEventListener(
            'scroll',
            handleViewportChange,
            true
        );

        return () => {
            window.removeEventListener(
                'resize',
                handleViewportChange
            );

            window.removeEventListener(
                'scroll',
                handleViewportChange,
                true
            );
        };
    }, [
        isOpen,
        updateDropdownPosition,
    ]);

    const closeMenu = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }

        setIsOpen(false);
        setIsPinned(false);
        setActiveCategory(null);
        setExpandedSections({});
        setTopProductData(null);
        setDropdownStyle(null);

        currentSkuRef.current = null;
    }, []);

    const openMenu = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }

        setIsOpen(true);

        setActiveCategory((currentCategory) => {
            return currentCategory ?? categoriesWithSale[0] ?? null;
        });
    }, [categoriesWithSale]);

    const cancelClose = useCallback(() => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
        }
    }, []);

    const handleToggleClick = useCallback(
        (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (isOpen && isPinned) {
                closeMenu();
                return;
            }

            setIsPinned(true);
            openMenu();
        },
        [
            closeMenu,
            isOpen,
            isPinned,
            openMenu,
        ]
    );

    const handleMenuLeave = useCallback(() => {
        if (isPinned) {
            return;
        }

        closeTimeout.current = setTimeout(() => {
            closeMenu();
        }, 200);
    }, [
        closeMenu,
        isPinned,
    ]);

    useEffect(() => {
        if (!isPinned) {
            return undefined;
        }

        const handleOutsideClick = (event) => {
            const clickedInsideMenu =
                menuRef.current?.contains(
                    event.target
                );

            const clickedInsideDropdown =
                dropdownRef.current?.contains(
                    event.target
                );

            if (
                !clickedInsideMenu &&
                !clickedInsideDropdown
            ) {
                closeMenu();
            }
        };

        document.addEventListener(
            'mousedown',
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleOutsideClick
            );
        };
    }, [
        closeMenu,
        isPinned,
    ]);

    const handleCategoryHover = useCallback((category) => {
        setActiveCategory(category);
        setExpandedSections({});
    }, []);

    const toggleExpand = useCallback((sectionKey) => {
        setExpandedSections((previous) => ({
            ...previous,
            [sectionKey]: !previous[sectionKey],
        }));
    }, []);

    useEffect(() => {
        const sku = getProductAttributes(
            activeCategory?.topProduct
        ).sku;

        currentSkuRef.current = sku || null;

        if (!sku) {
            setTopProductData(null);
            return;
        }

        if (productCache.current[sku]) {
            setTopProductData(productCache.current[sku]);
            return;
        }

        getStrapiProducts({
            ...DEFAULT_QUERY_GET_PRODUCTS,
            filters: {
                sku: {
                    $eq: sku,
                },
            },
        });
    }, [
        activeCategory?.topProduct,
        getStrapiProducts,
    ]);

    useEffect(() => {
        const sku = currentSkuRef.current;

        if (!sku || !Array.isArray(products)) {
            return;
        }

        const product = products.find((item) => {
            return getProductAttributes(item).sku === sku;
        });

        if (product) {
            productCache.current[sku] = product;
            setTopProductData(product);
        }
    }, [products]);

    if (!mounted) {
        return null;
    }

    const shouldShowOccasionExplorer =
        !isActiveCategorySale &&
        activeCategory &&
        Boolean(OCCASION_CHIPS[activeCategory.slug]?.length);

    const dropdownContent = isOpen && dropdownStyle && (
        <div
            ref={dropdownRef}
            className="menu-categories-dropdown"
            onMouseEnter={cancelClose}
            onMouseLeave={handleMenuLeave}
            style={{
                ...dropdownStyle,
                minHeight: 180,
                display: 'flex',
                boxSizing: 'border-box',
                background: '#fff',
                border: '1px solid #e0e6ed',
                boxShadow:
                    '0 10px 35px rgba(0, 0, 0, 0.12)',
                zIndex: 100000,
                overflow: 'hidden',
            }}
        >
            {isPinned && (
                <button
                    type="button"
                    onClick={closeMenu}
                    aria-label="Menü schließen"
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: 0,
                        borderRadius: 6,
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 18,
                        zIndex: 100001,
                    }}
                >
                    <FontAwesomeIcon
                        icon={faXmark}
                    />
                </button>
            )}

            <div
                style={{
                    flex: '0 0 280px',
                    width: 280,
                    minWidth: 280,
                    boxSizing: 'border-box',
                    borderRight:
                        '1px solid #e0e6ed',
                    background: '#fafbfc',
                    padding: '16px 12px',
                    overflow: 'hidden',
                }}
            >
                {loading && (
                    <div style={{ padding: 12 }}>
                        Kategorien werden geladen …
                    </div>
                )}

                {!loading && error && (
                    <div
                        style={{
                            padding: 12,
                            color: '#b00020',
                        }}
                    >
                        Kategorien konnten nicht
                        geladen werden.
                    </div>
                )}

                {!loading &&
                    !error &&
                    categoriesWithSale.length > 0 && (
                        <ul
                            style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0,
                            }}
                        >
                            {categoriesWithSale.map(
                                (category) => {
                                    const icon =
                                        getIconComponent(
                                            category.icon
                                        );

                                    const categoryKey =
                                        getEntityKey(
                                            category,
                                            category.title
                                        );

                                    const isActive =
                                        activeCategory?.id ===
                                            category.id ||
                                        activeCategory?.documentId ===
                                            category.documentId;

                                    const isSale =
                                        category.slug ===
                                        SALE_CATEGORY_SLUG;

                                    return (
                                        <li
                                            key={categoryKey}
                                            onMouseEnter={() =>
                                                handleCategoryHover(
                                                    category
                                                )
                                            }
                                            style={{
                                                padding:
                                                    '8px 12px',
                                                cursor:
                                                    'pointer',
                                                backgroundColor:
                                                    isActive
                                                        ? '#e3f2fd'
                                                        : 'transparent',
                                                boxSizing:
                                                    'border-box',
                                            }}
                                        >
                                            <Link
                                                href={getCategoryUrl(
                                                    category
                                                )}
                                                style={{
                                                    display:
                                                        'flex',
                                                    alignItems:
                                                        'center',
                                                    minWidth: 0,
                                                    color:
                                                        isSale
                                                            ? '#d32f2f'
                                                            : 'inherit',
                                                    textDecoration:
                                                        'none',
                                                }}
                                            >
                                                {icon && (
                                                    <FontAwesomeIcon
                                                        icon={
                                                            icon
                                                        }
                                                        style={{
                                                            flex:
                                                                '0 0 18px',
                                                            width: 18,
                                                            height: 18,
                                                            marginRight: 8,
                                                        }}
                                                    />
                                                )}

                                                <span
                                                    style={{
                                                        minWidth: 0,
                                                        overflow:
                                                            'hidden',
                                                        textOverflow:
                                                            'ellipsis',
                                                        whiteSpace:
                                                            'nowrap',
                                                        fontWeight:
                                                            isSale
                                                                ? 700
                                                                : 'normal',
                                                    }}
                                                >
                                                    {
                                                        category.title
                                                    }
                                                </span>
                                            </Link>
                                        </li>
                                    );
                                }
                            )}
                        </ul>
                    )}
            </div>

            {isActiveCategorySale ? (
                <div
                    style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: 14,
                        padding: '24px 40px',
                        boxSizing: 'border-box',
                        background:
                            'linear-gradient(180deg, #fff5f5 0%, #ffffff 100%)',
                    }}
                >
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            background: '#fde8e8',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faPercent}
                            style={{
                                fontSize: 22,
                                color: '#d32f2f',
                            }}
                        />
                    </div>

                    <h3
                        style={{
                            margin: 0,
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#222',
                        }}
                    >
                        Unsere aktuellen Sale-Angebote
                    </h3>

                    <p
                        style={{
                            margin: 0,
                            maxWidth: 420,
                            fontSize: 13.5,
                            lineHeight: 1.5,
                            color: '#666',
                        }}
                    >
                        Kein festes Kategorie-Thema, sondern eine laufend
                        aktualisierte Auswahl reduzierter Artikel aus dem
                        gesamten Sortiment.
                    </p>

                    <div
                        style={{
                            display: 'flex',
                            gap: 20,
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            margin: '4px 0 8px',
                        }}
                    >
                        {SALE_USPS.map((usp, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: '#333',
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={usp.icon}
                                    style={{
                                        color: '#d32f2f',
                                        fontSize: 14,
                                    }}
                                />
                                {usp.text}
                            </div>
                        ))}
                    </div>

                    <Link
                        href={SALE_PAGE_HREF}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 22px',
                            borderRadius: 6,
                            background: '#d32f2f',
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 700,
                            textDecoration: 'none',
                        }}
                    >
                        Jetzt alle Sale-Artikel ansehen
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                </div>
            ) : (
                <div
                    style={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        display: 'flex',
                        gap: 16,
                        padding: '16px 0 16px 20px',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            flex: '1 1 0',
                            minWidth: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {activeCategory &&
                            activeCategory.children?.length >
                                0 && (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fit, minmax(160px, 1fr))',
                                        gap: 12,
                                        width: '100%',
                                        minWidth: 0,
                                    }}
                                >
                                    {activeCategory.children.map(
                                        (
                                            subcategory,
                                            index
                                        ) => {
                                            const items =
                                                subcategory.children ??
                                                [];

                                            const sectionKey =
                                                getEntityKey(
                                                    subcategory,
                                                    index
                                                );

                                            const expanded =
                                                Boolean(
                                                    expandedSections[
                                                        sectionKey
                                                    ]
                                                );

                                            const visibleItems =
                                                expanded
                                                    ? items
                                                    : items.slice(
                                                          0,
                                                          MAX_SUB_ITEMS
                                                      );

                                            return (
                                                <div
                                                    key={
                                                        sectionKey
                                                    }
                                                    style={{
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Link
                                                        href={getCategoryUrl(
                                                            subcategory
                                                        )}
                                                        style={{
                                                            display:
                                                                'block',
                                                            marginBottom: 8,
                                                            color:
                                                                'inherit',
                                                            textDecoration:
                                                                'none',
                                                        }}
                                                    >
                                                        <h4
                                                            style={{
                                                                margin: 0,
                                                                fontSize: 13,
                                                                fontWeight:
                                                                    'bold',
                                                                textDecoration:
                                                                    'underline',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {
                                                                subcategory.title
                                                            }
                                                        </h4>
                                                    </Link>

                                                    <ul
                                                        style={{
                                                            listStyle:
                                                                'none',
                                                            padding: 0,
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {visibleItems.map(
                                                            (
                                                                item,
                                                                itemIndex
                                                            ) => (
                                                                <li
                                                                    key={getEntityKey(
                                                                        item,
                                                                        `${sectionKey}-${itemIndex}`
                                                                    )}
                                                                    style={{
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Link
                                                                        href={getCategoryUrl(
                                                                            item
                                                                        )}
                                                                        style={{
                                                                            display:
                                                                                'block',
                                                                            overflow:
                                                                                'hidden',
                                                                            textOverflow:
                                                                                'ellipsis',
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                            color:
                                                                                'inherit',
                                                                            textDecoration:
                                                                                'none',
                                                                        }}
                                                                    >
                                                                        {
                                                                            item.title
                                                                        }
                                                                    </Link>
                                                                </li>
                                                            )
                                                        )}
                                                    </ul>

                                                    {items.length >
                                                        MAX_SUB_ITEMS && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                toggleExpand(
                                                                    sectionKey
                                                                )
                                                            }
                                                            style={{
                                                                padding:
                                                                    '4px 0',
                                                                border: 0,
                                                                background:
                                                                    'none',
                                                                color:
                                                                    '#1976d2',
                                                                cursor:
                                                                    'pointer',
                                                            }}
                                                        >
                                                            {expanded
                                                                ? 'weniger'
                                                                : `mehr (${
                                                                      items.length -
                                                                      MAX_SUB_ITEMS
                                                                  })`}
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            )}

                        {!loading &&
                            activeCategory &&
                            (!activeCategory.children ||
                                activeCategory.children.length ===
                                    0) && (
                                <div>
                                    Keine Unterkategorien
                                    vorhanden.
                                </div>
                            )}

                        {!loading && activeCategory && (
                            <Link
                                href={getCategoryUrl(
                                    activeCategory
                                )}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    marginTop: 16,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: '#1976d2',
                                    textDecoration: 'none',
                                }}
                            >
                                Alle {activeCategory.title}{' '}
                                Produkte anzeigen
                                <FontAwesomeIcon
                                    icon={faArrowRight}
                                    style={{ fontSize: 12 }}
                                />
                            </Link>
                        )}

                        {!loading && shouldShowOccasionExplorer && (
                            <OccasionExplorer
                                categorySlug={activeCategory.slug}
                                getStrapiProducts={getStrapiProducts}
                            />
                        )}

                        {loading && (
                            <div>
                                Kategorien werden geladen …
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            flex: '0 1 320px',
                            width: 320,
                            maxWidth: 320,
                            minWidth: 280,
                            minHeight: 0,
                            boxSizing: 'border-box',
                            borderLeft:
                                '1px solid #e0e6ed',
                            padding: '0 20px',
                            overflow: 'hidden',
                        }}
                    >
                        {activeCategory?.productBrands
                            ?.length > 0 && (
                            <div
                                style={{
                                    width: '100%',
                                    minWidth: 0,
                                    marginBottom: 16,
                                }}
                            >
                                <div
                                    style={{
                                        marginBottom: 10,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Marken
                                </div>

                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(2, minmax(0, 1fr))',
                                        gap: 10,
                                        width: '100%',
                                        minWidth: 0,
                                    }}
                                >
                                    {activeCategory.productBrands.map(
                                        (
                                            brand,
                                            brandIndex
                                        ) => (
                                            <Link
                                                key={getEntityKey(
                                                    brand,
                                                    brandIndex
                                                )}
                                                href={getBrandUrl(
                                                    brand
                                                )}
                                                style={{
                                                    display:
                                                        'block',
                                                    minWidth: 0,
                                                    textDecoration:
                                                        'none',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: 55,
                                                        boxSizing:
                                                            'border-box',
                                                        display:
                                                            'flex',
                                                        alignItems:
                                                            'center',
                                                        justifyContent:
                                                            'center',
                                                        padding: 8,
                                                        border: '1px solid #eee',
                                                        borderRadius: 8,
                                                        background:
                                                            '#fff',
                                                        cursor:
                                                            'pointer',
                                                        overflow:
                                                            'hidden',
                                                    }}
                                                >
                                                    {brand.imageUrl ? (
                                                        <img
                                                            src={
                                                                brand.imageUrl
                                                            }
                                                            alt={
                                                                brand.title
                                                            }
                                                            title={
                                                                brand.title
                                                            }
                                                            style={{
                                                                display:
                                                                    'block',
                                                                width:
                                                                    '100%',
                                                                maxWidth: 140,
                                                                maxHeight: 44,
                                                                objectFit:
                                                                    'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <span
                                                            style={{
                                                                maxWidth:
                                                                    '100%',
                                                                fontSize: 14,
                                                                fontWeight:
                                                                    600,
                                                                textAlign:
                                                                    'center',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            {
                                                                brand.title
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                            </Link>
                                        )
                                    )}
                                </div>
                            </div>
                        )}

                        {topProductData && (
                            <>
                                <div
                                    style={{
                                        marginBottom: 10,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    Top Produkt
                                </div>

                                <div
                                    style={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <ProductGroupWithCarousel
                                        products={[
                                            topProductData,
                                        ]}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <>
            <style>{`
                .ps-product--card {
                    margin-bottom: 0 !important;
                }
            `}</style>

            <div
                ref={menuRef}
                className="menu--product-categories"
                style={{
                    position: 'relative',
                }}
                onMouseEnter={cancelClose}
                onMouseLeave={handleMenuLeave}
            >
                <button
                    ref={toggleRef}
                    type="button"
                    className="menu__toggle"
                    onMouseEnter={() => {
                        cancelClose();
                        openMenu();
                    }}
                    onClick={handleToggleClick}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    style={{
                        cursor: 'pointer',
                        border: 0,
                        background: 'transparent',
                    }}
                >
                    <i
                        className="icon-menu"
                        aria-hidden="true"
                    />

                    <span>Kategorien</span>
                </button>

                {dropdownContent &&
                    createPortal(
                        dropdownContent,
                        document.body
                    )}
            </div>
        </>
    );
}